/**
 * Google integration API
 */

import { fetchApi } from './fetch';

export interface GoogleIntegrationStatus {
  connected: boolean;
  scopes?: string[];
  expiresAt?: string;
  readCalendarIds?: string[];
  writeCalendarId?: string | null;
}

export interface GoogleCalendar {
  id: string;
  name: string;
  primary: boolean;
  color: string | null;
  accessRole: string | null;
}

export async function getGoogleIntegrationStatus(): Promise<GoogleIntegrationStatus> {
  return fetchApi<GoogleIntegrationStatus>('/integrations/google/status');
}

export async function disconnectGoogle(): Promise<void> {
  await fetchApi('/integrations/google', { method: 'DELETE' });
}

/**
 * Start the Google OAuth consent flow.
 *
 * The browser request carries session cookies so the server can identify the
 * user. Normally we navigate the current window. But inside the extension side
 * panel the app runs in an iframe, and Google refuses to render its consent
 * screen framed (X-Frame-Options) — the iframe just shows a 403. So when we
 * detect an embedded context we break out to a top-level popup (the same
 * approach social login uses in LoginPage). The popup relays completion back to
 * this window via postMessage + BroadcastChannel and closes itself; see
 * `relayGoogleConnectIfPopup` (main.tsx) and the listener in IntegrationsTab.
 *
 * @param win injectable for testing; defaults to the real window.
 */
export function connectGoogle(win: Window = window): void {
  const url = `${win.location.origin}/api/v1/integrations/google/connect`;
  const embedded = win.self !== win.top;
  if (embedded) {
    const popup = win.open(url, "_blank");
    if (popup) return;
    // Popup blocked — fall back to navigating the frame (better than nothing).
  }
  win.location.href = url;
}

/**
 * Google Calendar API
 */

export async function getGoogleCalendars(): Promise<{ calendars: GoogleCalendar[] }> {
  return fetchApi<{ calendars: GoogleCalendar[] }>('/calendar/calendars');
}

export interface CalendarPreferences {
  connected: boolean;
  plannerEnabled: boolean;
  plannerStartHour: number;
  plannerEndHour: number;
  plannerDefaultDurationMinutes: number;
  plannerMaxTasks: number;
  eventTitlePrefix: string | null;
  plannerPrompt: string | null;
  readCalendarIds: string[];
  writeCalendarId: string | null;
}

export async function getCalendarPreferences(): Promise<CalendarPreferences> {
  return fetchApi<CalendarPreferences>('/calendar/preferences');
}

export async function saveCalendarPreferences(prefs: {
  readCalendarIds?: string[];
  writeCalendarId?: string | null;
  plannerEnabled?: boolean;
  plannerPrompt?: string | null;
  plannerMaxTasks?: number;
  eventTitlePrefix?: string | null;
  plannerStartHour?: number;
  plannerEndHour?: number;
}): Promise<void> {
  await fetchApi('/calendar/preferences', {
    method: 'PATCH',
    body: JSON.stringify(prefs),
  });
}
